import { Fragment, h } from 'preact'; /** @jsx h */
import { useEffect, useState } from 'preact/hooks';
import { RowWrapper } from './FormWrappers';
import { getPostArea } from './apiFunctions';
import {
    Email,
    FirstName,
    FreeTextOption,
    HiddenInput,
    LastName,
    Mobile,
    NumberInput,
    PaymentRadioButtons,
    TextInput
} from './inputModules';
import { findInSession, setInSession } from './variables';

export const firstInputElements = (paymentError, formUrl, key, kamp, blockCode) => (
    <Fragment>
        {paymentError && paymentError === 1 && (
            <p className="form-global-error" role="alert">
                Noe gikk galt med kortbetaling til leverandøren vår. Kanskje du kan velge en annen
                betalingsmåte eller bruke ett annet kort.
            </p>
        )}
        <HiddenInput key={key} value={formUrl} name="formUrl" />
        <HiddenInput key={`${key}-kamp`} name="kamp" value={kamp} />
        <HiddenInput name="blockkode" value={blockCode} />
    </Fragment>
);

export const loggedInInfo = (paymentError, formUrl, user, kamp, blockCode) => (
    <Fragment>
        {firstInputElements(paymentError, formUrl, 'informFrmUrlKey', kamp, blockCode)}
        <p className="inFormText">
            <strong>Du er logget inn som</strong>
        </p>
        <p className="inFormText">
            {user.ho_fnavn} {user.ho_enavn}
        </p>
        <p className="inFormText">{user.adresse1}</p>
        <p className="inFormText">
            {user.postnr} {user.poststed}
        </p>
        <a className="inFormText logoutLink" href={'/medl/loggut.php?uri=' + formUrl}>
            Logg ut
        </a>
        <HiddenInput value={user.ho_fnavn + ' ' + user.ho_enavn} name="fra_navn" />
    </Fragment>
);

export const personInfoDetails = (
    formData,
    changeFormData,
    isReadOnly,
    postLookUp,
    postArea,
    key,
    useWebNames
) => (
    <Fragment>
        <RowWrapper grow>
            <FirstName
                key={`${key}Fname`}
                value={formData.fName}
                setVal={e => changeFormData('fName', e)}
                required
                name={useWebNames ? 'fname' : 'ho_fnavn'}
            />
            <LastName
                key={`${key}Lname`}
                value={formData.lName}
                setVal={e => changeFormData('lName', e)}
                required
                name={useWebNames ? 'lname' : 'ho_enavn'}
            />
        </RowWrapper>
        <TextInput
            key={`${key}AddressField`}
            lbl="Adresse"
            value={formData.streetName}
            setVal={e => changeFormData('streetName', e)}
            name="adresse1"
        />
        <RowWrapper className="mobile-half">
            <TextInput
                key={`${key}PostArea`}
                addClasses="third-size"
                lbl="Postnummer"
                value={formData.postalVal}
                setVal={e => changeFormData('postalVal', e)}
                setOnBlur={e => postLookUp(e)}
                pattern="[0-9]{4}"
                name={useWebNames ? 'zip' : 'postnr'}
                required
                infoTxt="Bruk 0000 for utenlandske adresser"
            />
            <TextInput
                key={`${key}AreaField`}
                addClasses={isReadOnly() ? 'non-border' : ''}
                lbl="Sted"
                value={postArea}
                name={useWebNames ? 'city' : 'poststed'}
                readonly={isReadOnly()}
                tabIndex={isReadOnly() ? '-1' : '0'}
                required
            />
        </RowWrapper>
        <TextInput
            key={`${key}CountryField`}
            lbl="Land"
            value={formData.country}
            setVal={e => changeFormData('country', e)}
            name="land"
            hidden={formData.postalVal !== '0000'}
        />
        <Email
            key={`${key}EmailField`}
            value={formData.email}
            setVal={e => changeFormData('email', e)}
            infoTxt="Ja takk, jeg er over 18 år og ønsker å motta e-post om Stiftelsen Norsk Luftambulanses livsviktige arbeid"
            name={useWebNames ? 'email' : 'ho_epost'}
        />
    </Fragment>
);

export const SumStep = ({
    titleStep1,
    infoText,
    paymentError,
    formUrl,
    kamp,
    lbl,
    optionList,
    value,
    setVal,
    onFocus,
    id,
    level = 2,
    blockCode,
    isStudent
}) => {
    const HeaderTag = `h${level}`;

    return (
        <Fragment>
            <HeaderTag>{titleStep1}</HeaderTag>
            {infoText && <p className="step-info-text">{infoText}</p>}
            <HiddenInput key="student" name="student" value={isStudent ? 'J' : 'N'} />
            {firstInputElements(paymentError, formUrl, 'supFormUrl', kamp, blockCode)}
            <FreeTextOption
                lbl={lbl}
                id={`paymentAmount${id}`}
                options={optionList}
                pattern="^[\d ]*$"
                setVal={setVal}
                onFocus={() => onFocus(false)}
                value={value}
                name="sum"
                type="number"
                min={50}
                invalidTxt="Felt må inneholde kun tall. Minstesum er 50kr."
                required
            />
        </Fragment>
    );
};

export const ContactStep = ({
    changeFormData,
    phoneNumber,
    email,
    children,
    isAnonymous,
    level = 2,
    emailRequired = false,
    infoTxt
}) => {
    const HeaderTag = `h${level}`;

    return (
        <Fragment>
            <HeaderTag>Kontaktinformasjon</HeaderTag>
            {children}
            {!isAnonymous && (
                <Fragment>
                    <Mobile
                        key="supMobileNo"
                        value={phoneNumber}
                        setVal={e => changeFormData('phoneNumber', e)}
                        name="phone"
                        required
                    />
                    <Email
                        key="subEmailField"
                        value={email}
                        setVal={e => changeFormData('email', e)}
                        infoTxt={
                            infoTxt ||
                            'Ja takk, jeg er over 18 år og ønsker å motta nyhetsbrev om Stiftelsen Norsk Luftambulanses livsviktige arbeid'
                        }
                        name="email"
                        required={emailRequired}
                    />
                </Fragment>
            )}
        </Fragment>
    );
};

export const PersonInfoStep = ({
    changeFormData,
    setFormData,
    fName,
    lName,
    streetName,
    postalVal,
    postArea,
    country,
    consent,
    payOptions,
    isDataFromLookup,
    level = 2,
    hideHeader,
    origin = ''
}) => {
    const HeaderTag = `h${level}`;

    const [addArea, setAddArea] = useState(false);

    const postLookUp = event => {
        if (event.target && event.target.value && event.target.value.length === 4) {
            const postnr = event.target.value;

            getPostArea({ postnummer: postnr }).then(response => {
                if (response && response.poststed)
                    setFormData(formData => ({ ...formData, postArea: response.poststed }));
                else setAddArea(true);
            });
        }
    };

    const isReadOnly = !(postalVal === '0000' || addArea);

    const isWill = origin === 'will';

    return (
        <Fragment>
            {!hideHeader && (
                <HeaderTag>
                    {isDataFromLookup ? 'Vi fant følgende informasjon' : 'Navn og adresse'}{' '}
                </HeaderTag>
            )}
            <RowWrapper grow>
                <FirstName
                    key="subFname"
                    value={fName}
                    setVal={e => changeFormData('fName', e)}
                    required
                    name="fname"
                />
                <LastName
                    key="subLname"
                    value={lName}
                    setVal={e => changeFormData('lName', e)}
                    required
                    name="lname"
                />
            </RowWrapper>
            <TextInput
                key="subAddressField"
                lbl="Adresse"
                value={streetName}
                setVal={e => changeFormData('streetName', e)}
                name="adresse1"
            />
            <RowWrapper className="mobile-half">
                <TextInput
                    key="subPostArea"
                    addClasses="third-size"
                    lbl="Postnummer"
                    value={postalVal}
                    setVal={e => changeFormData('postalVal', e)}
                    setOnBlur={e => postLookUp(e)}
                    pattern="[0-9]{4}"
                    name="zip"
                    required
                    infoTxt="Bruk 0000 for utenlandske adresser"
                    invalidTxt={`${
                        /^\d+$/.test(postalVal) ? '' : 'Postnummer kan kun inneholde siffer. '
                    }${postalVal.length !== 4 ? 'Postnummer må inneholde akkurat 4 tall.' : ''}`}
                />
                <TextInput
                    key="subAreaField"
                    addClasses={isReadOnly ? 'non-border' : ''}
                    lbl="Sted"
                    value={postArea}
                    setVal={e => changeFormData('postArea', e)}
                    name="city"
                    readonly={isReadOnly}
                    tabIndex={isReadOnly ? '-1' : '0'}
                    required
                />
            </RowWrapper>
            {postalVal === '0000' && (
                <TextInput
                    key="subCountryField"
                    lbl="Land"
                    value={country}
                    setVal={e => changeFormData('country', e)}
                    name="land"
                />
            )}
            {isWill && (
                <div className="checkbox-container">
                    <input
                        type="checkbox"
                        id="consent"
                        className="consentCheckbox"
                        name="consent"
                        checked={consent === 'J'}
                        onChange={e => changeFormData('consent', e)}
                        value={consent}
                    />
                    <label htmlFor="consent">
                        Ja, jeg ønsker mer informasjon om fremtidsfullmakt, testament og arbeidet
                        til Stiftelsen Norsk Luftambulanse i posten.
                    </label>
                </div>
            )}
            {payOptions && payOptions.length === 1 && (
                <HiddenInput key="supPayment" value={payOptions[0].value} name="betalingsform" />
            )}
        </Fragment>
    );
};

export const PaymentStep = ({
    payOptions,
    setVal,
    formId,
    level = 2,
    hasFradrag = true,
    recLine
}) => {
    const HeaderTag = `h${level}`;

    return (
        <Fragment>
            <HeaderTag>Velg betalingsmåte</HeaderTag>
            {recLine && <>{recLine}</>}
            {payOptions && payOptions.length > 1 && (
                <PaymentRadioButtons
                    key="supPayOptions"
                    name="betalingsform"
                    options={payOptions}
                    changeInfo="payOptions"
                    setVal={setVal}
                    formId={formId}
                />
            )}
            {hasFradrag && (
                <p className="infotext">
                    Samlet gavesum over 500 kroner gir rett til skattefradrag. Vi rapporterer alle
                    gaver automatisk til skattemyndigheter.
                </p>
            )}
        </Fragment>
    );
};
export const FormInfo = props => {
    const { fName, lName, streetName, postalVal, postArea, country, phoneNumber } = props;

    const [editMode, setEditMode] = useState(false);
    // eslint-disable-next-line camelcase
    const firstName = fName;
    // eslint-disable-next-line camelcase
    const surName = lName;
    return (
        <Fragment>
            {editMode ? (
                <PersonInfoStep hideHeader {...props} />
            ) : (
                <Fragment>
                    <p className="inFormText">
                        <strong>Din kontaktinfo</strong>
                    </p>
                    <p className="inFormText">
                        {firstName} {surName}
                    </p>
                    <p className="inFormText">{streetName}</p>
                    <p className="inFormText">
                        {postalVal} {postArea}
                    </p>
                    <HiddenInput key="giftUsrFName" name="fname" value={fName} />
                    <HiddenInput key="giftUsrEName" name="lname" value={lName} />
                    <HiddenInput key="giftUsrStreet" name="adresse1" value={streetName} />
                    <HiddenInput key="giftUsrPostNo" name="zip" value={postalVal} />
                    <HiddenInput key="giftUsrPostSted" name="city" value={postArea} />
                    <HiddenInput key="giftUsrCountry" name="country" value={country} />
                    <HiddenInput key="giftUsrPhone" name="phone" value={phoneNumber} />
                    <button
                        type="button"
                        className="back-btn hollow-btn"
                        onClick={() => setEditMode(!editMode)}
                    >
                        endre kontaktinfo
                    </button>
                </Fragment>
            )}
        </Fragment>
    );
};
// eslint-disable-next-line camelcase
export const UserInfo = ({ formUrl, ho_fnavn, ho_enavn, adresse1, postnr, poststed }) => {
    // eslint-disable-next-line camelcase
    const firstName = ho_fnavn;
    // eslint-disable-next-line camelcase
    const surName = ho_enavn;

    return (
        <Fragment>
            <p className="inFormText">
                <strong>Du er logget inn som</strong>
            </p>
            <p className="inFormText">
                {firstName} {surName}
            </p>
            <p className="inFormText">{adresse1}</p>
            <p className="inFormText">
                {postnr} {poststed}
            </p>
            <a className="inFormText logoutLink" href={'/medl/loggut.php?uri=' + formUrl}>
                Logg ut
            </a>
        </Fragment>
    );
};

export const LoginStep = ({
    loginCode,
    setLoginCode,
    phoneNumber,
    formUrl,
    level = 2,
    loginError
}) => {
    const HeaderTag = `h${level}`;

    return (
        <Fragment>
            <HeaderTag>Logg inn</HeaderTag>
            <p className="logintxt">
                Hvis du allerede støtter oss, vil du straks motta en firesifret kode på SMS som du
                må taste inn her for å logge inn.
            </p>
            <TextInput
                value={loginCode}
                setVal={e => setLoginCode(e.target.value)}
                key="giftSmsCode"
                addClasses="third-size"
                lbl="Kode fra SMS"
                pattern="[0-9]{4}"
                name="phoneNumber-code"
                required
                autoComplete="off"
            />
            {loginError && (
                <p className="login-invalidText" aria-live="polite">
                    Innlogg feilet. Sjekk at koden er riktig og prøv igjen eller fortsett uten å
                    logge inn.
                </p>
            )}
            <HiddenInput
                key="giftMemberPhone"
                name="phoneNumber"
                value={phoneNumber.replace(/\s/g, '')}
            />
            <HiddenInput key="giftMemberAction" name="action" value="pin" />
            <HiddenInput key="giftMemberRedirect" name="redirect" value={formUrl} />
        </Fragment>
    );
};

export const SkipLogin = ({ onClick }) => {
    return (
        <button type="button" className="submitBtn secondary" onClick={onClick}>
            Fortsett uten å logge inn
        </button>
    );
};

// parser str til integer.
// hvis det feiler så returneres 1
const parseNumber = str => {
    const valueInt = parseInt(str, 10);

    return isNaN(valueInt) ? 1 : valueInt;
};

export const NumberStep = ({
    titleStep1,
    paymentError,
    formUrl,
    kamp,
    lbl,
    startNumber,
    price,
    id,
    level = 2,
    blockCode
}) => {
    const [number, setNumber] = useState(startNumber);

    useEffect(() => {
        // number lagres i session i tilfelle feil ved betaling
        const sessionSum = findInSession(formUrl);

        if (sessionSum) {
            setNumber(parseNumber(sessionSum));
        }
    }, []);

    const HeaderTag = `h${level}`;

    return (
        <Fragment>
            <HeaderTag>{titleStep1}</HeaderTag>
            {firstInputElements(paymentError, formUrl, 'supFormUrl', kamp, blockCode)}
            <RowWrapper>
                <NumberInput
                    lbl={lbl}
                    value={number}
                    setVal={e => {
                        const { value } = e.target;

                        setNumber(parseNumber(value));
                        // number lagres i session i tilfelle feil ved betaling
                        setInSession(formUrl, value);
                    }}
                    name="number"
                    required={true}
                    addClasses="quarter-size"
                    min="1"
                    max="100"
                    id={`numberInput${id}`}
                />
                <p className="small-info-txt">{price * number}NOK</p>
            </RowWrapper>
        </Fragment>
    );
};
